/**
 * Money Module
 *
 * Centralized money handling for Dnceclub.
 * All money values are stored internally as cents (integers) to avoid floating point errors.
 *
 * Usage:
 *   import { Money, calculateFeeBreakdown, formatCurrency } from '@dnceclub/shared';
 *
 *   const price = Money.fromDollars(100);
 *   const breakdown = calculateFeeBreakdown(price, { customerFeeRatio: 0.5 });
 *   console.log(formatCurrency(Money.fromCents(breakdown.customerTotal)));
 */

// Core Money class
export { Money } from './Money';

// Types
export type {
  Cents,
  FeeConfig,
  FeeBreakdown,
  FeeBreakdownWithCredit,
  RefundBreakdown,
} from './types';

// Constants
export {
  DEFAULT_PLATFORM_FEE_PERCENT,
  DEFAULT_STRIPE_FEE_PERCENT,
  DEFAULT_STRIPE_FEE_FIXED,
  DEFAULT_CUSTOMER_FEE_RATIO,
  DEFAULT_FEE_CONFIG,
} from './constants';

// Fee calculations
export {
  calculateFeeBreakdown,
  calculateFeeBreakdownWithCredit,
  calculateTotalFeesFromAmounts,
  calculatePlatformFee,
  calculateStripeFee,
} from './calculations/fee-calculator';

// Refund calculations
export {
  calculateRefundBreakdown,
  calculateRefundFee,
  calculateNetRefund,
} from './calculations/refund';

// Formatting
export {
  formatCurrency,
  formatCents,
  formatDollars,
  formatFeeBreakdown,
  formatFeeBreakdownWithCredit,
  formatRefundBreakdown,
  feeBreakdownToDollars,
} from './formatting';

export type {
  FormattedFeeBreakdown,
  FormattedFeeBreakdownWithCredit,
  FormattedRefundBreakdown,
} from './formatting';

// ============================================================================
// Simple Conversion Helpers
// ============================================================================

/**
 * Converts dollars to cents for database storage.
 * Use this when inserting money values into INTEGER columns that store cents.
 *
 * @param dollars - The amount in dollars
 * @returns The amount in cents (rounded integer)
 */
export function dollarsToCents(dollars: number | undefined | null): number {
  if (dollars === undefined || dollars === null || isNaN(dollars)) {
    return 0;
  }
  return Math.round(dollars * 100);
}

/**
 * Converts cents to dollars for API responses.
 * Use this at API boundaries when returning money values to the frontend.
 *
 * @param cents - The amount in cents
 * @returns The amount in dollars
 */
export function centsToDollars(cents: number | undefined | null): number {
  if (cents === undefined || cents === null || isNaN(cents)) {
    return 0;
  }
  return cents / 100;
}
